import {CommentsCategory, useComments, type CommentDto} from '@domain/comments'
import {useUpmPlantContext} from '@hconnect/upm'

import {useSelectedTimeframe, useMaterialOrderPermissions} from '../providers'

import {useMaterialOrdersQuery} from './useMaterialOrdersQuery'
import {useUrlParam} from './useUrlParam'

/**
 * Hook to return comments for orders in a selected timeframe for a specific materialId.
 * Should be used when the materialId URL param is available.
 */
export const useMaterialOrdersCommentsByOrderId = () => {
  const {plantId: plantCode} = useUpmPlantContext()
  const materialIdParam = useUrlParam('materialId')
  const materialId = Number(materialIdParam)

  const {selectedTimeframe} = useSelectedTimeframe()
  const [start, end] = selectedTimeframe ?? []

  const {data: materialOrders} = useMaterialOrdersQuery({
    plantCode,
    materialId,
    from: start?.toISOString(),
    to: end?.toISOString(),
    options: {enabled: Boolean(selectedTimeframe)}
  })

  const materialOrdersIds = materialOrders?.map(({id}) => id) ?? []

  const {canViewComments} = useMaterialOrderPermissions()

  const shouldEnableQuery = materialOrdersIds.length > 0 && canViewComments

  return useComments<Record<string, CommentDto>>(
    {
      plantId: plantCode,
      commentsCategory: CommentsCategory.MaterialOrders,
      commentCategoryInstanceIds: materialOrdersIds.map((id) => String(id))
    },
    'comments',
    {
      select: (comments) =>
        comments.reduce<Record<string, CommentDto>>((dict, comment) => {
          // NOTE: only one comment is possible per material order
          dict[comment.commentsCategoryInstanceId] = comment
          return dict
        }, {}),
      enabled: shouldEnableQuery
    }
  )
}
